/**
 * Hook qui retourne la vue d'affichage de la machine actuellement active
 * (en bridging | connected_to_agent | qualified), ou null s'il n'y en a pas.
 *
 * Premier vrai consommateur de la machine (CallSheet) — lecture seule.
 *
 * Le pool est exposé sur window.__callPool par useShadowCallPool. Ce hook
 * s'abonne à ses changements et re-render à chaque transition.
 */

import { useEffect, useState } from 'react';
import type { CallActor, CallPool } from './callPool';
import type { EnrichedContact } from './callMachine';

type StateValue = 'bridging' | 'connected_to_agent' | 'qualified';

const ACTIVE_STATES: ReadonlySet<string> = new Set(['bridging', 'connected_to_agent', 'qualified']);

function getPool(): CallPool | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as { __callPool?: CallPool }).__callPool ?? null;
}

function getActiveSnap(): { actor: CallActor | null; stateValue: StateValue | null } {
  const pool = getPool();
  if (!pool) return { actor: null, stateValue: null };
  const actor = pool.getActiveMachine() ?? null;
  if (!actor) return { actor: null, stateValue: null };
  const snap = actor.getSnapshot();
  const value: string = typeof snap.value === 'string' ? snap.value : (Object.keys(snap.value)[0] ?? '');
  if (!ACTIVE_STATES.has(value)) return { actor: null, stateValue: null };
  return { actor, stateValue: value as StateValue };
}

export interface ActiveCallView {
  /** L'actor de la machine active (pour send des events). */
  actor: CallActor | null;
  /** État actuel ou null. */
  stateValue: StateValue | null;
  /** True quand la machine est en bridging | connected_to_agent | qualified. */
  showCallSheet: boolean;
  /** True quand l'agent peut qualifier (qualified state). */
  callEnded: boolean;
  /** Le contact enrichi exposé par la machine. */
  connectedContact: EnrichedContact | null;
}

export function useActiveCallActor(): ActiveCallView {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, force] = useState(0);

  useEffect(() => {
    const pool = getPool();
    if (!pool) return;
    return pool.subscribe(() => force((v) => v + 1));
  }, []);

  const { actor, stateValue } = getActiveSnap();
  if (!actor || !stateValue) {
    return { actor: null, stateValue: null, showCallSheet: false, callEnded: false, connectedContact: null };
  }
  return {
    actor,
    stateValue,
    showCallSheet: true,
    callEnded: stateValue === 'qualified',
    connectedContact: actor.getSnapshot().context.enrichedContact,
  };
}
